/**
 * Query parameters that carry credentials in URLs our telemetry records: provider API keys in outbound
 * dependencies (Nearmap `apikey`, OpenEI `api_key`, Mapbox `access_token`, Google `key`), and magic-link
 * `token` / OAuth `code` in inbound request URLs.
 */
const SECRET_QUERY_PARAM = /(^|[?&;])((?:apikey|api_key|key|access_token|token|sig|client_secret|code|password)=)[^&#\s]*/gi;

const MASKED_SECRET = 'REDACTED';

/** Replaces the value of every credential-bearing query parameter; also accepts a bare query string. */
export function maskUrlSecrets(value: string): string {
  return value.replace(SECRET_QUERY_PARAM, `$1$2${MASKED_SECRET}`);
}

/** Span attributes the Azure Monitor exporter turns into a request or dependency URL. */
const URL_ATTRIBUTES = ['url.full', 'url.query', 'http.url', 'http.target'] as const;

type SpanWithAttributes = {
  attributes: Readonly<Record<string, unknown>>;
  setAttribute(key: string, value: string): unknown;
};

/**
 * An OpenTelemetry span processor for the server SDK. The classic `addTelemetryProcessor` is a no-op in
 * applicationinsights 3.x. HTTP instrumentation sets URL attributes when it creates the span, so they
 * are already present in `onStart`, while the span is still writable.
 */
export function createUrlSecretMaskingSpanProcessor() {
  return {
    onStart(span: SpanWithAttributes) {
      for (const key of URL_ATTRIBUTES) {
        const value = span.attributes[key];
        if (typeof value !== 'string') continue;
        const masked = maskUrlSecrets(value);
        if (masked !== value) span.setAttribute(key, masked);
      }
    },
    onEnd() {},
    forceFlush: () => Promise.resolve(),
    shutdown: () => Promise.resolve()
  };
}

type AppInsightsShimConfig = { azureMonitorOpenTelemetryOptions?: { spanProcessors?: object[] } };

/**
 * Adds the masking processor to the classic SDK's config. The shim reads these options when `start()`
 * builds the OpenTelemetry pipeline, so this has to run between `setup()` and `start()`.
 */
export function addUrlSecretMasking(config: AppInsightsShimConfig): void {
  const options = config.azureMonitorOpenTelemetryOptions;
  config.azureMonitorOpenTelemetryOptions = {
    ...options,
    spanProcessors: [...(options?.spanProcessors ?? []), createUrlSecretMaskingSpanProcessor()]
  };
}
